import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import {
  Button,
    Tab,
  Tabs,
} from "@mui/material";
import CreateVendor from "./CreateVendor";
import { useDispatch, useSelector } from "react-redux";
import {
  ShowAllVendors,
  deleteUser,
} from "../../../../context/Slice/RegisterSlice";
import Swal from "sweetalert2";
import "./VendorManagement.css";
import VendorDetailsToAdmin from "./vendorDetailstoAdmin";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL

const VendorManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const vendors = useSelector((state) => state.RegisterR.vendors);
  const [searchText, setSearchText] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState("active");

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setOpenImage(true);
  };

  const handleCloseImage = () => {
    setOpenImage(false);
    setSelectedImage(null);
  };

  useEffect(() => {
    dispatch(ShowAllVendors());
  }, [dispatch]);


  const filteredVendors = useMemo(() => {
    const vendorList = vendors?.vendorRole || []; // Get vendors from vendorRole if it exists

    // Function to format services into a comma-separated string
    const getServicesString = (services) => {
      return (
        services?.map((service) => service.ServiceName).join(", ") || "N/A"
      );
    };

    if (!searchText) {
      return vendorList
        .filter((vendor) => vendor.status === activeTab)
        .map((vendor) => ({
          ...vendor,
          OwnerCity: vendor?.Vendor?.[0]?.OwnerCity || "N/A", // Extract OwnerCity or use 'N/A' if not available
          servicesString: getServicesString(vendor.Services), // Add formatted services string
        }));
    }

    return vendorList
    .filter((data) => {
      const search = searchText.toLowerCase();
      return (
        data.firstName?.toLowerCase().includes(search) ||
        data.lastName?.toLowerCase().includes(search) ||
        data.email?.toLowerCase().includes(search) ||
        (data.mobile_number && data.mobile_number.includes(search)) ||
        (typeof data.Verified === "string" && data.Verified.toLowerCase().includes(search)) || // ✅ Fix here
        (data.Vendor && data.Vendor[0]?.OwnerCity?.toLowerCase().includes(search)) ||
        (data.PinCode && data.PinCode.includes(search))
      );
    })
    .filter((vendor) => vendor.status === activeTab) // Filter based on activeTab status
    .map((vendor) => ({
      ...vendor,
      OwnerCity: vendor?.Vendor?.[0]?.OwnerCity || "N/A", // Extract OwnerCity or use 'N/A' if not available
      servicesString: getServicesString(vendor.Services), // Add formatted services string
    }));
    
  }, [vendors, searchText, activeTab]);

  const columns = [
    { field: "firstName", headerName: "First Name", width: 150 },
    { field: "lastName", headerName: "Last Name", width: 150 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "role", headerName: "Role", width: 150 },
    { field: "servicesString", headerName: "Category", width: 250 },
    { field: "OwnerCity", headerName: "City", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <div>
          <Button
            variant="contained"
            className="fs-5"
            sx={{ marginRight: "8px" }}
            onClick={() => handleView(params.row)}
          >
            View
          </Button>
          <Button
            variant="contained"
            className="fs-5"
            color={params.row.status === "active" ? "error" : "success"}
            onClick={() => handleDelete(params.row.id, params.row.status)} // Correctly passing status
          >
            {params.row.status === "active" ? "Disable" : "Enable"}
          </Button>
        </div>
      ),
    },
  ];

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleCreateUser = () => {
    setIsAdding(true);
  };

  const refreshUserList = () => {
    dispatch(ShowAllVendors());
  };

  const handleFormClose = () => {
    setIsAdding(false);
    setIsEditing(false);
  };

  const handleView = async (row) => {
    setViewMode(true);
    setSelectedVendor(null);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/v1/VendorManagement/show-ShowOneVendorDetail/${row.id}`
      );
      const vendorDetails = response.data.data?.Vendordetail?.[0];
      if (vendorDetails) {
        if (
          vendorDetails.OwnerImage &&
          typeof vendorDetails.OwnerImage === "string"
        ) {
          try {
            const parsedImages = JSON.parse(vendorDetails.OwnerImage);
            vendorDetails.OwnerImage = parsedImages.map(
              (image) =>
                `${BASE_URL}${image.path.replace(
                  "/home/CEP_BackEnd",
                  ""
                )}`
            );
          } catch (error) {
            console.error("Error parsing OwnerImage:", error);
            vendorDetails.OwnerImage = [];
          }
        } else {
          vendorDetails.OwnerImage = [];
        }
        setSelectedVendor(vendorDetails);
      } else {
        Swal.fire("Error", "Vendor details not found.", "error");
        setViewMode(false);
      }
    } catch (error) {
      console.error("Error fetching vendor details:", error);
      Swal.fire("Error", "Vendor has not yet updated his details.", "error");
      setViewMode(false);
    }
  };

  const handleDelete = (id, currentStatus) => {
    const isCurrentlyActive = currentStatus === "active";
    const actionText = isCurrentlyActive ? "Disable" : "Enable";
    const confirmButtonText = `Yes, ${actionText} it!`;
    const successMessage = `The Vendor has been ${
      isCurrentlyActive ? "disabled" : "enabled"
    }.`;

    Swal.fire({
      title: "Are you sure?",
      text: `${actionText} the Vendor data`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: confirmButtonText,
      customClass: {
        popup: "swal2-popup",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteUser(id)).then(() => {
          Swal.fire(`${actionText}d!`, successMessage, "success");
          dispatch(ShowAllVendors()); // Refresh the vendor list
        });
      }
    });
  };

  const HandleBack = () => {
    console.log("Back button clicked");
    navigate("/admin-dashboard");
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer className="MuiDataGrid-toolbarContainer">
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <React.Fragment>
      <Box sx={{ height: 400, width: "100%" }} id="Admin-Vendormanagement" className="data-grid-container">
        {!isAdding && !isEditing && !viewMode && (
          <>
            <h2 className="heading">Vendor Management</h2>
            {/* Back Button */}
            <Button
              variant="contained"
              color="secondary"
              onClick={HandleBack}
              sx={{ position: "absolute", top: "20px", left: "20px" }}
            >
              Back
            </Button>

            <div className="searchField">
              <input
                type="text"
                value={searchText}
                onChange={handleSearchChange}
                placeholder="Search"
                className="searchInput"
              />
            </div>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="Vendor status tabs"
              >
                <Tab label="Active Vendors" value="active" />
                <Tab label="Inactive Vendors" value="inactive" />
              </Tabs>
            </Box>

            <DataGrid
              rows={filteredVendors}
              columns={columns}
              pageSize={5}
              pageSizeOptions={[5, 10, 15]}
              checkboxSelection
              disableRowSelectionOnClick
              components={{ Toolbar: CustomToolbar }}
              sx={{
                "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
                  fontSize: "14px",  // Default font size for larger screens
                },
                "@media (max-width: 800px)": {
                  "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
                    fontSize: "12px",  // Font size for smaller screens
                  },
                  "& .MuiDataGrid-cell": {
                    padding: "5px",  // Reduce padding for smaller screens to make cells more compact
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "12px",  // Ensure column header title is also reduced
                  },
                },
                "@media (max-width: 500px)": {
                  "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
                    fontSize: "12px",  // Further reduce font size for very small screens
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "12px",  // Reduce column header title font size
                  },
                  "& .MuiDataGrid-cell": {
                    padding: "1px",  // Reduce padding even more for very small screens
                  },
                },
              }}
            />
          </>
        )}
        {isAdding && (
          <CreateVendor
            handleFormClose={handleFormClose}
            refreshUserList={refreshUserList}
          />
        )}
        {isEditing && <CreateVendor selectedBooking={selectedBooking} />}
        {viewMode && (
          <VendorDetailsToAdmin
            vendorDetails={selectedVendor}
            onBack={() => setViewMode(false)}
            openImage={openImage}
            selectedImage={selectedImage}
            handleImageClick={handleImageClick}
            handleCloseImage={handleCloseImage}
          />
        )}
      </Box>
    </React.Fragment>
  );
};

export default VendorManagement;
